import Cookies from "js-cookie";
import uniqueId from "lodash.uniqueid";

export default class Utils {
  static formatDate(s: any, type: any) {
    const date = Date.parse(s);
    if (isNaN(date)) return "";

    if (Utils.isToday(date)) return "Today";

    return new Intl.DateTimeFormat(
      "en-GB",
      typeof type === "string"
        ? type === "long"
          ? {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "numeric",
              hour12: true,
            }
          : {
              year: "numeric",
              month: "short",
              day: "numeric",
            }
        : type
    ).format(date);
  }

  static isToday(date: any) {
    if (typeof date.getDate !== "function") return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  static toApiDate(date: any) {
    if (!date) return null;
    const offset = date.getTimezoneOffset();
    const d = new Date(date.getTime() - offset * 60 * 1000);
    return d.toISOString().split("T")[0];
  }

  static formatReadableDate(date: any) {
    if (!date) return null;
    var yyyy = date.getFullYear().toString();
    var mm = (date.getMonth() + 1).toString();
    var dd = date.getDate().toString();

    return dd + "/" + mm + "/" + yyyy;
  }

  static parseApiDate(date: any) {
    return date ? new Date(Date.parse(date)) : date;
  }

  static formatProficiency(prof: any, valid: any) {
    return prof && prof > 0 && (valid === undefined || valid)
      ? Utils.formatNumber(prof)
      : "n/a";
  }

  static formatProficiencyFraction(
    currentProf: any,
    requiredProf: any,
    valid: any
  ) {
    return currentProf &&
      currentProf > 0 &&
      requiredProf &&
      requiredProf > 0 &&
      (valid === undefined || valid)
      ? Utils.formatProficiency(currentProf, valid) +
          " / " +
          Utils.formatProficiency(requiredProf, valid)
      : "n/a";
  }

  static formatPercent(n: any) {
    if (n === null || isNaN(n)) return "n/a";
    return Math.ceil(n) + "%";
  }

  static roundNumber(num: any, dec = 2) {
    return +(Math.round((num + "e+" + dec) as any) + "e-" + dec);
  }

  static zeroPadNumber(num: any, zeros: any) {
    return String(num).padStart(zeros, "0");
  }

  static formatNumber(num: any) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  static formatCurrency(num: any, dec = 0) {
    return parseInt(num, 10).toLocaleString("en-US", {
      minimumFractionDigits: dec,
    });
  }

  static setCookie(name: any, value: any, days = 7, path = "/") {
    const expires =
      days === -1
        ? "Thu, 01 Jan 1970 00:00:00 GMT"
        : new Date(Date.now() + days * 864e5).toUTCString();
    //document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=' + path + ';samesite=none';
    //document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=' + path + ';samesite=none; domain=localhost:9631';
    //document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=' + path + ';samesite=none; domain=localhost';

    //Cookies.set(name, value, { expires: days, path, sameSite: 'none' })

    //alert(document.cookie);
  }

  static getCookie(name: any) {
    return document.cookie.split("; ").reduce((r, v) => {
      const parts = v.split("=");
      return parts[0] === name ? decodeURIComponent(parts[1]) : r;
    }, "");
  }

  static truncate(input: any, limit: any) {
    if (!input) return input;
    return input.length > limit ? input.substring(0, limit) + "..." : input;
  }

  static deleteCookie(name: any, path: any) {
    Cookies.remove(name, { path, sameSite: "none" });
  }

  static getDevOption(key: any) {
    return window.localStorage.getItem(key) === "true";
  }

  static setDevOption(key: any, value: any) {
    window.localStorage.setItem(key, value);
  }

  static getLocalStorageOption(key: any) {
    return window.localStorage.getItem(key);
  }

  static checkLocalStorageOption(key: any) {
    return window.localStorage.getItem(key) === "true";
  }

  static setLocalStorageOption(key: any, value: any) {
    window.localStorage.setItem(key, value);
  }

  static removeLocationHash() {
    (window.history as any).pushState(
      "",
      document.title,
      window.location.pathname,
      window.location.search
    );
  }

  static setLocation(history: any, path: any, useNative: any) {
    if (useNative === true) window.history.pushState(null, "", path);
    else history.push(path);
  }

  static isValidEmail(email: any) {
    return email
      ? email.match(
          /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        ) !== null
      : false;
  }

  static isPasswordValid(password: any) {
    return Utils.validatePassword(password, 3);
  }

  static validatePassword(password: any, numRequirements = 3) {
    if (password.length < 8) return false;
    if (password.length > 64) return false;
    let checks = 0;
    if (password.search(/[a-z]/) !== -1) checks++;
    if (checks >= numRequirements) return true;
    if (password.search(/[A-Z]/) !== -1) checks++;
    if (checks >= numRequirements) return true;
    if (password.search(/[0-9]/) !== -1) checks++;
    if (checks >= numRequirements) return true;
    // A symbol is anything that is not a letter or digit, matching the on-screen
    // "A symbol (e.g. "-")" wording rather than a hardcoded character list.
    if (password.search(/[^a-zA-Z0-9]/) !== -1) checks++;
    return checks >= numRequirements;
  }

  static isTouchDevice() {
    return (
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.maxTouchPoints > 0
    );
  }

  static generateUniqueId(prefix: any) {
    return uniqueId(prefix);
  }

  static compareList(list1: any, list2: any, comparator: any) {
    if (!list1 || !list2) return false;
    if (list1.length !== list2.length) return false;
    let matches = true;
    list1.forEach((c: any) => {
      if (!list2.some((c2: any) => comparator(c2, c))) matches = false;
    });
    return matches;
  }

  static pluralize(count: any, noun: any, suffix = "s") {
    return Utils.formatNumber(count) + " " + noun + (count !== 1 ? suffix : "");
  }

  static isOrAre(count: any) {
    return count === 1 ? "is" : "are";
  }
}
